import styled from '@emotion/styled'
import { memo, useEffect, useMemo, useState } from 'react'
import {
  V2WithdrawalChainRpcMap,
  V2WithdrawalMulticall,
  V2WithdrawalNetworks,
  V2WithdrawalZkLinkChains,
} from './constants'
import { TokenItem } from './TokenItem'
import { V2WithdrawalTokens } from './tokens'
import { compact } from 'lodash'
import { Interface, hexlify, zeroPad } from 'ethers/lib/utils'
import withdrawalAbi from './abi.json'
import { useWeb3Address } from 'store/web3/hooks'
import { sendMulticall } from 'hooks/web3/multicall'
import { BigNumber, providers } from 'ethers'
import { Web3Provider } from '@ethersproject/providers'
import toastify from 'components/Toastify'
import { useSetAtom } from 'jotai'
import { v2WithdrawalBalanceAtom } from './atoms'
import { useInterval } from 'usehooks-ts'

const Wrap = styled('div')`
  width: 100%;
  max-width: 453px;
  height: 332px;
  padding: 12px 24px 16px;
  background-color: rgba(30, 35, 41, 1);
`
const Head = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(46, 54, 59, 1);
`
const ChainIcon = styled('img')`
  width: 32px;
  height: 32px;
  border-radius: 50%;
`
const Body = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
`

export const ChainItem = memo<{
  chainId: keyof typeof V2WithdrawalNetworks // 0x1
}>(({ chainId }) => {
  const { iconUrl, name, layerTwoChainId } = V2WithdrawalNetworks[chainId]
  const accountAddress = useWeb3Address()
  const updateBalances = useSetAtom(v2WithdrawalBalanceAtom)
  const [count, setCount] = useState<number>(0)

  useInterval(() => {
    setCount((prev) => prev + 1)
  }, 20000)

  const tokens: {
    symbol: string
    address: string
    chainId: number
    layerOneChainId: string
    tokenId: number
    decimals: number
  }[] = useMemo(() => {
    return compact(
      Object.values(V2WithdrawalTokens).map((t) => {
        if (t.chains[String(layerTwoChainId)]) {
          return {
            ...t.chains[String(layerTwoChainId)],
            layerOneChainId: chainId,
            tokenId: t.id,
            symbol: t.symbol,
          }
        } else {
          return null
        }
      }),
    )
  }, [])

  useEffect(() => {
    if (!chainId) return
    if (!accountAddress) return
    if (!layerTwoChainId) return
    if (!tokens?.length) return

    const iface = new Interface(withdrawalAbi)
    const fragment = iface.getFunction('getPendingBalance')

    const padAddress = hexlify(zeroPad(accountAddress, 32))

    const calls = tokens.map((token) =>
      iface.encodeFunctionData(fragment, [padAddress, token.tokenId]),
    )
    const zklinkChain = V2WithdrawalZkLinkChains.find(
      (v) => Number(v.chainId) === Number(layerTwoChainId),
    )

    if (!zklinkChain) {
      toastify.error(`Chain not found on zkLink, ${layerTwoChainId}`)
      return
    }

    const callAddresses = tokens.map(() => zklinkChain.mainContract)

    const rpc = V2WithdrawalChainRpcMap[chainId]
    const provider = new providers.JsonRpcProvider(rpc, {
      chainId: Number(chainId),
      name: '',
    })

    const multicallAddress = V2WithdrawalMulticall[chainId]

    sendMulticall<BigNumber>(
      provider as Web3Provider,
      multicallAddress,
      callAddresses,
      calls,
    )
      .then(({ decode }) => {
        const balances = decode(iface, fragment).map((v) => v.toString())

        const balancesMap: Record<string, string> = {}
        tokens.forEach((token, i) => {
          balancesMap[token.tokenId] = balances[i]
        })

        updateBalances((prev) => {
          return {
            ...prev,
            [chainId]: balancesMap,
          }
        })
      })
      .catch((e) => {
        console.error(e)
      })
  }, [tokens, chainId, accountAddress, layerTwoChainId, count])

  return (
    <Wrap>
      <Head>
        <ChainIcon src={iconUrl} />
        <span>{name}</span>
      </Head>
      <Body>
        {tokens?.map((token) => {
          return <TokenItem key={token.symbol} token={token} />
        })}
      </Body>
    </Wrap>
  )
})
